/**
 * Image URL interceptor to prevent invalid image URLs from causing 404 errors
 * This can be imported and initialized in the main app
 */

const DEFAULT_AVATAR = '/uploads/avatars/default.jpg';

export function initializeImageInterceptor() {
    // Store original methods and constructor
    const OriginalImage = window.Image;
    const originalSetAttribute = Element.prototype.setAttribute;
    const originalImageSetter = Object.getOwnPropertyDescriptor(HTMLImageElement.prototype, 'src')?.set;
    const originalImageGetter = Object.getOwnPropertyDescriptor(HTMLImageElement.prototype, 'src')?.get;
    
    // Override setAttribute for img elements
    Element.prototype.setAttribute = function(this: Element, name: string, value: string) {
        // Ensure this is actually an Element before proceeding
        if (!(this instanceof Element)) {
            console.error('setAttribute called on non-Element:', this);
            return;
        }
        
        if (name === 'src' && this.tagName === 'IMG' && /^\/?\d+$/.test(value)) {
            console.warn(`Invalid image src detected on img element: "${value}". Using default avatar.`);
            console.trace('Stack trace for invalid image URL:');
            originalSetAttribute.call(this, name, DEFAULT_AVATAR);
        } else {
            originalSetAttribute.call(this, name, value);
        }
    };
    
    // Override the src property setter on HTMLImageElement prototype
    if (originalImageSetter) {
        Object.defineProperty(HTMLImageElement.prototype, 'src', {
            set: function(this: HTMLImageElement, value: string) {
                if (/^\/?\d+$/.test(value)) {
                    console.warn(`Invalid image URL detected: "${value}". Using default avatar.`);
                    console.trace('Stack trace for invalid image URL:');
                    originalImageSetter.call(this, DEFAULT_AVATAR);
                } else {
                    originalImageSetter.call(this, value);
                }
            },
            get: function(this: any) {
                try {
                    // First try the original getter if available
                    if (originalImageGetter) {
                        return originalImageGetter.call(this);
                    }
                    // Only use getAttribute if this is definitely an Element with that method
                    if (this && this instanceof Element && typeof this.getAttribute === 'function') {
                        return this.getAttribute('src') || '';
                    }
                } catch (e) {
                    // Silently fail and return empty string
                    console.debug('Error in src getter:', e);
                }
                return '';
            },
            configurable: true
        });
    }
    
    // Override Image constructor
    (window as any).Image = function(this: HTMLImageElement, width?: number, height?: number) {
        const img = new OriginalImage(width, height);
        
        // Create a proxy to intercept property access
        return new Proxy(img, {
            set(target, prop, value) {
                if (prop === 'src' && typeof value === 'string' && /^\/?\d+$/.test(value)) {
                    console.warn(`Invalid image URL in Image constructor: "${value}". Using default avatar.`);
                    console.trace('Stack trace for invalid image URL:');
                    target.src = DEFAULT_AVATAR;
                    return true;
                }
                (target as any)[prop] = value;
                return true;
            }
        });
    };
    
    // Copy static properties
    Object.setPrototypeOf(window.Image, OriginalImage);
    Object.setPrototypeOf(window.Image.prototype, OriginalImage.prototype);
}

export function validateImageUrl(url: string | null | undefined): string {
    if (!url || /^\/?\d+$/.test(url)) {
        return DEFAULT_AVATAR;
    }
    return url;
}